// Client-side behaviour for tab strips rendered by
// templates/ssr/macros/tabs.html. framework.js loads this on every page, so
// pages never import it: they listen for the change event below, and switch
// a tab from code by clicking its button.

export function selectTab(container, value) {
  const target = container.querySelector('[role="tab"][data-tab="' + value + '"]');
  if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true') return false;
  container.querySelectorAll('[role="tab"]').forEach((tab) => {
    const selected = tab === target;
    tab.setAttribute('aria-selected', selected ? 'true' : 'false');
    tab.classList.toggle('active', selected);
    tab.classList.toggle('btn-primary', selected);
    tab.classList.toggle('btn-ghost', !selected);
  });
  return true;
}

// Every selection ends with `tab-strip:change` on the strip (it bubbles), so a
// page runs its own side effects (lazy loads, redraws) without its own click
// handler: strip.addEventListener('tab-strip:change', (e) => e.detail.value).
function announce(strip, tab) {
  strip.dispatchEvent(new CustomEvent('tab-strip:change', {
    bubbles: true,
    detail: { value: tab.dataset.tab, tab },
  }));
}

// Button tabs switch on the client. A strip with data-tab-panels also shows
// the selected tab's panel and hides the others: a tab names its panel
// through aria-controls (the macro's `controls`) or, without it, by a panel id
// equal to the tab value.
function selectButtonTab(event) {
  const tab = event.target.closest && event.target.closest('[data-tab-strip] > button[role="tab"]');
  if (!tab) return;
  const strip = tab.parentElement;
  if (!selectTab(strip, tab.dataset.tab)) return;
  if (strip.hasAttribute('data-tab-panels')) {
    strip.querySelectorAll(':scope > button[role="tab"]').forEach((other) => {
      const panel = document.getElementById(other.getAttribute('aria-controls') || other.dataset.tab);
      if (panel) panel.classList.toggle('hidden', other !== tab);
    });
  }
  announce(strip, tab);
}

// Reflect an HTMX tab's selection and hidden input on click. Tabs that swap
// only a panel never get the strip re-rendered, and for the rest a request
// fired before the response lands (e.g. Show All) still reads the open tab.
function selectHtmxTab(event) {
  const tab = event.target.closest && event.target.closest('[data-tab-strip] > a[role="tab"]');
  if (!tab) return;
  const strip = tab.parentElement;
  if (!selectTab(strip, tab.dataset.tab)) return;
  const input = strip.querySelector(':scope > input[type="hidden"]');
  if (input) input.value = tab.dataset.tab;
  announce(strip, tab);
}

// Button tabs replaced native radio groups, so keep their arrow-key movement:
// Left/Right focus and click the neighbouring enabled tab, Home/End the first
// and last one. HTMX link tabs are skipped so arrowing across them does not
// fire a request per key press.
const TAB_KEYS = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];

function moveBetweenTabs(event) {
  if (!TAB_KEYS.includes(event.key)) return;
  const tab = event.target.closest && event.target.closest('[data-tab-strip] > button[role="tab"]');
  if (!tab) return;
  const tabs = Array.from(tab.parentElement.querySelectorAll(':scope > button[role="tab"]:not(:disabled):not(.hidden)'));
  if (tabs.length < 2) return;
  let next;
  if (event.key === 'Home') next = tabs[0];
  else if (event.key === 'End') next = tabs[tabs.length - 1];
  else next = tabs[(tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
  event.preventDefault();
  next.focus();
  next.click();
}

if (!window.__smaacTabStripKeysBound) {
  window.__smaacTabStripKeysBound = true;
  document.addEventListener('keydown', moveBetweenTabs);
  document.addEventListener('click', selectHtmxTab);
  document.addEventListener('click', selectButtonTab);
}
